'use client'
import { useSearchType } from '@/api/hooks'
import Loader from '@/app/components/loader'
import { clickableAdWidth } from '@/app/components/spotlight-grid/spotlight-util'
import ClickableAd from '@/app/components/spotlight-or-video-grid/clickable-ad'
import SpotlightContainer from '@/app/components/spotlight-or-video-grid/container'
import VideoSpotlight from '@/app/components/video-spotlight-grid'
import { AdDecision } from '@/app/util/ads-endpoint'
import { SearchTypeCopy, SearchTypeToFetchType } from '@/app/util/search-util'
import { useGiphyFetch } from '@/app/util/use-gf'
import { useIsMobile } from '@/app/util/use-media-query'
import Grid from '@/components/grid/grid'
import { useAdOffsets } from '@/components/grid/use-ad-offsets'
import VideoGrid from '@/components/grid/video-grid'
import { SearchTypeContext } from '@/context/search-type'
import UAParserContext from '@/context/ua-parser'
import isAdInResult from '@/util/ad-in-result'
import useClientRender from '@/util/use-client-render'
import { GifsResult } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { useSearchParams } from 'next/navigation'
import { useCallback, useContext, useRef } from 'react'
import { useWindowSize } from 'react-use'

const getTrackingUrl = (page: number, term: string) => `/search/${term}/${page}`

type Props = {
    term: string
    initialGifs?: IGif[]
    videoData?: AdDecision
    clickableAd?: AdDecision
}

const SearchGrids = ({ term, initialGifs, videoData, clickableAd }: Props) => {
    const { current: initialSearchTerm } = useRef(term)
    const { isTransitioning } = useContext(SearchTypeContext)
    const type = useSearchType()
    const { isBot } = useContext(UAParserContext)
    const params = useSearchParams()
    const gf = useGiphyFetch()

    const isMobile = useIsMobile()
    const clientRender = useClientRender()
    const { width: windowWidth } = useWindowSize()

    const hasBouncerGridAd = initialGifs && isAdInResult(initialGifs)

    const fetchGifs = useCallback(
        async (offset: number) =>
            gf.search(initialSearchTerm, {
                offset,
                limit: 15,
                type: SearchTypeToFetchType[type],
            }),
        [gf, initialSearchTerm, type]
    )
    const fakeFetchGifs = () =>
        Promise.resolve({
            data: [],
            meta: { response_id: '', msg: 'OK', status: 404 },
            pagination: { total_count: 0, offset: 0, count: 0 },
        } as GifsResult)

    const { columnOffsets } = useAdOffsets({
        clickableAdGif: clickableAd?.gif || null,
        spotlightGif: videoData?.gif || null,
        includeSpotlight: !!videoData,
        isMobile,
        windowWidth,
        type,
        clientRender,
    })

    return isTransitioning ? (
        <Loader />
    ) : (
        <>
            {type === 'clips' ? (
                <VideoGrid
                    initialGifs={initialGifs}
                    fetchGifs={isBot ? fakeFetchGifs : fetchGifs}
                    key={`${term + type + params.toString()}`}
                    gaTrackingUrl={(page) => getTrackingUrl(page, initialSearchTerm)}
                />
            ) : (
                <>
                    {!hasBouncerGridAd && (
                        <>
                            {videoData?.gif ? (
                                <SpotlightContainer>
                                    <VideoSpotlight data={videoData} className="mb-1.5 md:mb-0" />
                                </SpotlightContainer>
                            ) : (
                                clickableAd?.gif &&
                                clientRender &&
                                type === 'gifs' && (
                                    <ClickableAd
                                        ad={clickableAd}
                                        gif={clickableAd.gif}
                                        width={isMobile ? windowWidth / 2 - 3 : clickableAdWidth}
                                        isMobile={isMobile}
                                    />
                                )
                            )}
                        </>
                    )}
                    <Grid
                        columnOffsets={columnOffsets}
                        initialGifs={initialGifs}
                        key={`${term + type + params.toString()}`}
                        fetchGifs={isBot ? fakeFetchGifs : fetchGifs}
                        gaTrackingUrl={(page) => getTrackingUrl(page, initialSearchTerm)}
                        noShowMessage={
                            <>
                                No {SearchTypeCopy[type]} found for {initialSearchTerm}.<br />
                                Try searching for {SearchTypeCopy[type === 'gifs' ? 'stickers' : 'gifs']} instead?
                            </>
                        }
                        blockPlaceholder={hasBouncerGridAd || !!clickableAd}
                    />
                </>
            )}
        </>
    )
}
export default SearchGrids
